// Bungee Jumping Simulator — entry point.
//
// Responsibilities kept here (everything else lives in its own module):
//   - Three.js scene, camera, renderer setup
//   - Level management: one shared scene, a swappable `activeLevel`
//   - Camera positioning (follows activeLevel.player) and yaw/pitch rotation
//   - Wiring InputManager → Game / Level / Audio
//   - Frame loop

import * as THREE from 'three';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader.js';
import { InputManager } from './input.js';
import { Game } from './game.js';
import { PlaygroundLevel, MoonLevel, HellLevel } from './level.js';
import { UI } from './ui.js';
import { AssetLoader } from './assets.js';
import { AudioManager } from './audio.js';

// ─── Scene & renderer ─────────────────────────────────────────────

const scene = new THREE.Scene();

const camera = new THREE.PerspectiveCamera(
	45,
	window.innerWidth / window.innerHeight,
	0.1,
	9000, // headroom for the 8000-unit water plane + 4500-scale sky dome
);

const renderer = new THREE.WebGLRenderer({ antialias: true });
renderer.setPixelRatio(window.devicePixelRatio);
renderer.setSize(window.innerWidth, window.innerHeight);
// ACES tone mapping is required for the Sky shader (HDR output). With the
// composer pipeline below, tone mapping is applied by OutputPass at the
// end — the renderer setting is read from there. Exposure calibrated
// against sun + hemisphere intensities in level.js; change both together.
renderer.toneMapping = THREE.ACESFilmicToneMapping;
renderer.toneMappingExposure = 0.5;
// Soft PCF shadows. Single shadow map (not CSM) — fine for the island
// frustum; revisit if a level enlarges the scene dramatically.
renderer.shadowMap.enabled = true;
renderer.shadowMap.type = THREE.PCFSoftShadowMap;
document.body.appendChild(renderer.domElement);

// ─── Post-processing pipeline ─────────────────────────────────────
// RenderPass (HDR) → FXAA (edge AA) → OutputPass (tone map + sRGB).
// Reused across level swaps — it references `scene`/`camera`, both of
// which persist; only the scene *contents* change.
//
// Bloom removed: the Sky shader emits 30-50× normal brightness near
// the sun (Mie scattering peak), and UnrealBloomPass spreads that HDR
// energy into a huge blown-out region no matter how low the strength.
// Putting OutputPass before bloom caused double gamma encoding, which
// blew out the entire sky to white. Future option: selective bloom
// masking the sky, or a clamp pre-pass.
const composer = new EffectComposer(renderer);
composer.setPixelRatio(window.devicePixelRatio);
composer.setSize(window.innerWidth, window.innerHeight);
composer.addPass(new RenderPass(scene, camera));

const fxaa = new ShaderPass(FXAAShader);
function _setFxaaResolution() {
	const dpr = renderer.getPixelRatio();
	fxaa.material.uniforms['resolution'].value.set(
		1 / (window.innerWidth * dpr),
		1 / (window.innerHeight * dpr),
	);
}
_setFxaaResolution();
composer.addPass(fxaa);

composer.addPass(new OutputPass());

// ─── Game + UI + Audio ─────────────────────────────────────────────

const game = new Game();
const ui = new UI(game, renderer.domElement);

// Procedural Web Audio cues. The AudioContext is created lazily inside
// resume(), which we fire on the first PLAYING transition — that's driven
// by a click or keypress, satisfying the browser autoplay gesture rule.
const audio = new AudioManager();
game.on('stateChange', () => {
	if (game.isPlaying) audio.resume();
	else if (game.isPaused) audio.pause();
});

// ─── View state (yaw/pitch) ───────────────────────────────────────

const SPAWN_YAW = 0;
// Cinematic spawn pose: look out toward the horizon, tilted ~20° down so the
// island/ocean fills the lower screen — not the old straight-down stare. On
// jump we ease the pitch down to JUMP_PITCH to mimic a diver's body rotating
// head-first toward the ground.
const SPAWN_PITCH = THREE.MathUtils.degToRad(-20);
const JUMP_PITCH = THREE.MathUtils.degToRad(-80);
const JUMP_TILT_LAMBDA = 2.2; // ease rate (1/s) of the post-jump downward tilt
const MOUSE_SENSITIVITY = 0.003;
const PITCH_LIMIT = Math.PI / 2 - 0.001;

let yaw = SPAWN_YAW;
let pitch = SPAWN_PITCH;
let jumpTilt = false; // auto-easing pitch toward JUMP_PITCH after a jump

function resetView() {
	yaw = SPAWN_YAW;
	pitch = SPAWN_PITCH;
	jumpTilt = false;
}

// Horizontal launch heading = the camera's forward (-Z) rotated by yaw, then
// flattened to the ground plane. Lets the jump fire in the direction you're
// facing (azimuth only — pitch is ignored). Reuses a scratch vector; the
// level copies it into the player velocity, so sharing it is safe.
const _heading = new THREE.Vector3();
function lookHeading() {
	return _heading.set(-Math.sin(yaw), 0, -Math.cos(yaw));
}

// ─── Level management ──────────────────────────────────────────────
// One shared THREE.Scene; each level adds/removes its own contents via
// build()/dispose(). `activeLevel` is the indirection that lets input,
// audio, and the loop always talk to "the current level" — setLevel()
// swaps it, tearing the old one down and rebinding everything to the new.
//
// LEVELS is the debug-cycle order, stepped by the `cycleLevel` action (a dev
// hotkey). The hell respawn reaches HellLevel through the death handler
// (goToLevel) rather than the cycle, but it's listed here too so KeyL can
// reach it directly for testing.
const LEVELS = [PlaygroundLevel, MoonLevel, HellLevel];
let levelIndex = 0;
let activeLevel = null;
let assetsReady = false;
let transitioning = false; // guards the death→respawn fade against re-entry

// (Re)subscribe per-level event handlers. Called once per level instance.
// The outgoing level's listeners die with it when it's dereferenced, so
// there's nothing to manually unsubscribe.
function bindLevelEvents(level) {
	// jump → whoosh. (The level also emits `catch`, currently unconsumed —
	// the synthesized twang was dropped.)
	level.on('jump', (speed) => audio.whoosh(speed));
	// death → the hell respawn (or escape back to the island).
	level.on('death', () => onDeath());
}

// Swap the active level. Synchronous, so the loop (which only runs on
// requestAnimationFrame) never sees a half-built world between frames:
// dispose the outgoing level (frees its GPU resources), build the incoming
// one into the shared scene, hand it any already-loaded assets, rebind
// events, and recenter the view.
function setLevel(next) {
	if (activeLevel) activeLevel.dispose();
	activeLevel = next;
	activeLevel.build(scene);
	if (assetsReady) activeLevel.addGLTFTrees(assets.assets);
	activeLevel.configureRenderer(renderer);
	bindLevelEvents(activeLevel);
	resetView();
}

// Swap to a specific level class (keeping the debug cycle index in sync so
// KeyL keeps making sense afterward).
function goToLevel(LevelClass) {
	const i = LEVELS.indexOf(LevelClass);
	levelIndex = i >= 0 ? i : levelIndex;
	setLevel(new LevelClass());
}

function cycleLevel() {
	levelIndex = (levelIndex + 1) % LEVELS.length;
	setLevel(new LEVELS[levelIndex]());
}

// The cut-cord easter egg landed: fade to black, swap worlds behind it, fade
// back in. From anywhere → hell; from hell → escape back to the island.
async function onDeath() {
	if (transitioning) return;
	transitioning = true;
	const escapingHell = activeLevel instanceof HellLevel;
	await ui.fadeOut(650);
	goToLevel(escapingHell ? PlaygroundLevel : HellLevel);
	await ui.fadeIn(900);
	transitioning = false;
}

// ─── Asset loading ─────────────────────────────────────────────────
// GLTF tree variants are optional (see public/models/README) — if either
// file is missing, AssetLoader soft-fails to null and that species simply
// doesn't appear. Currently wired to Quaternius's Stylized Nature MegaKit
// (CC0): Pine_1 → "pine" (high elevations), CommonTree_1 → "palm" stand-in
// (low ring; the MegaKit has no palms). Swap variants by changing the URLs.
const assets = new AssetLoader();
assets.addGLTF('pineGLTF', 'models/Pine_1.gltf');
assets.addGLTF('palmGLTF', 'models/CommonTree_1.gltf');
assets.addFake('warmup-1', 220);
assets.addFake('warmup-2', 340);

assets.on('progress', (loaded, total) => ui.updateLoading(loaded, total));
assets.on('complete', () => {
	assetsReady = true;
	// Hand the just-loaded assets to whatever level is currently built.
	activeLevel.addGLTFTrees(assets.assets);
	game.loadingDone();
});

// Build the starting level now (before assets resolve, matching the old
// flow — trees get added in the 'complete' handler above), then load.
setLevel(new LEVELS[levelIndex]());
assets.load();

// ─── Input ────────────────────────────────────────────────────────

const input = new InputManager(renderer.domElement);

input.on('look', (dx, dy) => {
	if (game.isPaused) return;
	jumpTilt = false; // a manual look cancels the post-jump auto-tilt
	yaw -= dx * MOUSE_SENSITIVITY;
	pitch -= dy * MOUSE_SENSITIVITY;
	if (pitch > PITCH_LIMIT) pitch = PITCH_LIMIT;
	if (pitch < -PITCH_LIMIT) pitch = -PITCH_LIMIT;
});

input.on('jump', () => {
	if (game.isMenu) game.start();
	if (game.isPlaying) {
		// First press launches the jump (in the direction you're facing); a
		// press while already falling cuts the cord (the hell easter egg).
		// Both are no-ops at the wrong time.
		if (activeLevel.inJump) {
			activeLevel.cutCord();
		} else {
			activeLevel.triggerJump(lookHeading());
			jumpTilt = true; // ease the view down as the dive begins
		}
	}
});

input.on('pause', () => {
	game.togglePause();
});

input.on('restart', () => {
	activeLevel.reset();
	resetView();
});

// DEBUG (Phase 5.1): rebuild / step through levels at runtime. Replaced by
// a proper level selector in Phase 6.
input.on('cycleLevel', () => {
	cycleLevel();
});

// ─── Window resize ────────────────────────────────────────────────

window.addEventListener('resize', () => {
	camera.aspect = window.innerWidth / window.innerHeight;
	camera.updateProjectionMatrix();
	renderer.setSize(window.innerWidth, window.innerHeight);
	composer.setSize(window.innerWidth, window.innerHeight);
	_setFxaaResolution();
});

// ─── Loop ─────────────────────────────────────────────────────────

const yawQ = new THREE.Quaternion();
const pitchQ = new THREE.Quaternion();
const X_AXIS = new THREE.Vector3(1, 0, 0);
const Y_AXIS = new THREE.Vector3(0, 1, 0);

let lastTime = performance.now();

function loop(now) {
	let dt = (now - lastTime) / 1000;
	if (dt > 1 / 15) dt = 1 / 15; // clamp big spikes (same as legacy)
	lastTime = now;

	// Update the level whenever the world should "feel alive" — animated
	// water, sky shimmer, etc. tick on the splash screen too. Pause and
	// loading freeze everything. Physics is gated separately inside Level.
	if (!game.isPaused && !game.isLoading) {
		activeLevel.update(dt);
	}

	// HUD shows during play and pause (frozen at last values while paused).
	if (game.isPlaying || game.isPaused) {
		ui.updateHUD(activeLevel.player.position, activeLevel.player.velocity);
	}

	// Wind intensity tracks fall speed (only while playing — pause suspends
	// the audio context, so feeding it would be wasted).
	if (game.isPlaying) {
		audio.setSpeed(activeLevel.player.velocity.length());
	}

	// Cinematic dive tilt: after a jump, ease the pitch down toward JUMP_PITCH
	// until it settles (or the player grabs the view manually, which clears
	// jumpTilt in the look handler).
	if (jumpTilt && game.isPlaying) {
		pitch = THREE.MathUtils.damp(pitch, JUMP_PITCH, JUMP_TILT_LAMBDA, dt);
		if (Math.abs(pitch - JUMP_PITCH) < 0.005) {
			pitch = JUMP_PITCH;
			jumpTilt = false;
		}
	}

	// Camera follows player; orientation is yaw × pitch (no roll).
	camera.position.copy(activeLevel.player.position);
	yawQ.setFromAxisAngle(Y_AXIS, yaw);
	pitchQ.setFromAxisAngle(X_AXIS, pitch);
	camera.quaternion.multiplyQuaternions(yawQ, pitchQ);

	composer.render();
	requestAnimationFrame(loop);
}
requestAnimationFrame(loop);
